class CartUpsellVariantSelector extends HTMLElement {
  constructor() {
    super();
    
    // Only initialize if within cart upsell context
    if (!this.closest('product-info-cart-upsell')) return;
    
    this.addEventListener('change', this.onVariantChange.bind(this));
    this.variantData = JSON.parse(this.dataset.variants || '[]');
    this.currentVariant = parseInt(this.dataset.currentVariant);
    this.updateOptions();
    this.updateVariantSelection();
  }

  updateOptions() {
    // Only select elements within cart upsell context
    if (!this.closest('product-info-cart-upsell')) return;
    const selects = Array.from(this.querySelectorAll('select'));
    this.selectedOptions = selects.map(select => select.value);
  }

  onVariantChange() {
    // Ensure we're in cart upsell context
    if (!this.closest('product-info-cart-upsell')) return;
    this.updateOptions();
    this.updateVariantSelection();
  }

  updateVariantSelection() {
    if (!this.variantData || !this.variantData.length) return;

    const matchingVariant = this.variantData.find((variant) => 
      variant.options.every((option, index) => option === this.selectedOptions[index])
    );

    if (matchingVariant) {
      const form = this.closest('product-info-cart-upsell').querySelector('form[data-type="add-to-cart-form"]');
      if (form) {
        const input = form.querySelector('input[name="id"]');
        if (input) {
          input.value = matchingVariant.id;

          const submitButton = form.querySelector('[data-cart-upsell-submit]');
          const buttonText = submitButton?.querySelector('.button-text');
          if (submitButton && buttonText) {
            if (matchingVariant.available) {
              submitButton.removeAttribute('disabled');
              buttonText.textContent = 'ADD';
            } else {
              submitButton.setAttribute('disabled', '');
              buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
            }
          }
        }
      }
    }
  }
}

class CartUpsellForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitWrapper = this.querySelector('[data-cart-upsell-submit]');
    this.submitButton = this.querySelector('.cart-upsell-card__add-btn-inner') || this.querySelector('button[type="submit"]');
    this.buttonText = this.querySelector('.button-text');
    this.spinner = this.querySelector('.cart-upsell-card__spinner');

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    // Handle variant select changes (supports multiple option selects)
    const variantSelectsWrapper = this.form?.querySelector('[data-upsell-variant-selects]');
    if (variantSelectsWrapper) {
      // Load variant data from product JSON
      const productCard = this.closest('.cart-upsell-card');
      const jsonScript = productCard?.querySelector('script[type="application/json"]');
      const productData = jsonScript ? JSON.parse(jsonScript.textContent) : null;
      const variants = productData?.variants || [];

      variantSelectsWrapper.addEventListener('change', () => {
        // Get selected values from all option selects
        const selects = Array.from(variantSelectsWrapper.querySelectorAll('.cart-upsell-card__variant-select'));
        const selectedOptions = selects.map(s => s.value);

        // Find matching variant
        const matchingVariant = variants.find(v =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );

        if (matchingVariant) {
          const idInput = this.form.querySelector('input[name="id"]');
          if (idInput) idInput.value = matchingVariant.id;

          // Update price display
          const priceWrapper = productCard?.querySelector('[data-price-wrapper]');
          if (priceWrapper) {
            const currentPrice = priceWrapper.querySelector('.cart-upsell-card__current-price');
            if (currentPrice) currentPrice.textContent = Shopify.formatMoney ? Shopify.formatMoney(matchingVariant.price) : ('£' + (matchingVariant.price / 100).toFixed(2));
            const comparePrice = priceWrapper.querySelector('.cart-upsell-card__compare-price');
            if (comparePrice) {
              if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
                comparePrice.textContent = Shopify.formatMoney ? Shopify.formatMoney(matchingVariant.compare_at_price) : ('£' + (matchingVariant.compare_at_price / 100).toFixed(2));
                comparePrice.style.display = '';
              } else {
                comparePrice.style.display = 'none';
              }
            }
          }

          // Update ADD button state
          const btn = this.submitButton?.querySelector('button') || this.submitButton;
          const btnText = this.buttonText;
          if (btn && btnText) {
            if (matchingVariant.available) {
              btn.removeAttribute('disabled');
              btnText.textContent = 'ADD';
            } else {
              btn.setAttribute('disabled', '');
              btnText.textContent = 'Sold out';
            }
          }
        }
      });
    }
  }

  async onSubmit(evt) {
    evt.preventDefault();
    evt.stopPropagation();

    if (!this.submitButton || this.submitButton.hasAttribute('disabled')) return;

    this.setLoadingState(true);

    try {
      const formData = new FormData();
      formData.append('id', this.form.querySelector('input[name="id"]').value);
      formData.append('quantity', '1');
      formData.append('form_type', 'product');
      formData.append('utf8', '✓');
      formData.append('sections', 'cart-drawer');

      const response = await fetch(window.Shopify.routes.root + 'cart/add', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || 'Error adding item to cart');
      }

      await this.updateCartDrawer();

    } catch (error) {
      this.showError(error.message);
    } finally {
      this.setLoadingState(false);
    }
  }

  async updateCartDrawer() {
    const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
    if (!response.ok) return;

    const html = await response.text();
    const parser = new DOMParser();
    const drawerContent = parser.parseFromString(html, 'text/html').querySelector('.drawer__inner');

    if (drawerContent) {
      document.querySelector('.drawer__inner').innerHTML = drawerContent.innerHTML;
    }

    // Fix progress bar fill (Dawn's div:empty rule hides it)
    const progressBar = document.getElementById('cart-progress-bar');
    if (progressBar && progressBar.dataset.progress) {
      progressBar.style.display = 'block';
      progressBar.style.width = progressBar.dataset.progress + '%';
    }

    // Re-init upsell arrow states
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.initArrowStates === 'function') {
      setTimeout(() => cartDrawer.initArrowStates(), 200);
    }

    // Re-populate delivery estimate
    if (typeof window.updateDeliveryEstimate === 'function') window.updateDeliveryEstimate();
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.submitButton.setAttribute('disabled', '');
      this.buttonText?.classList.add('hidden');
      this.spinner?.classList.remove('hidden');
    } else {
      this.submitButton.removeAttribute('disabled');
      this.buttonText?.classList.remove('hidden');
      this.spinner?.classList.add('hidden');
    }
  }

  showError(message) {
    const errorWrapper = this.querySelector('.product-form__error-message-wrapper');
    const errorMessage = this.querySelector('.product-form__error-message');
    
    if (errorWrapper && errorMessage) {
      errorWrapper.removeAttribute('hidden');
      errorMessage.textContent = message;
      setTimeout(() => errorWrapper.setAttribute('hidden', ''), 5000);
    }
  }
}

if (!customElements.get('cart-upsell-form')) {
  customElements.define('cart-upsell-form', CartUpsellForm);
}

// Note: cart-drawer-variant-selects custom element removed — variant selection
// is now handled inline by CartUpsellForm via [data-upsell-variant-selects]